import getIdFromIdIri from '../../shared/parsers/getIdFromIdIri';

/**
 * Parses raw company data into a structured `ICompany` object.
 *
 * @param {any} data - The raw company data received from the API.
 * @returns {ICompany} A structured company object with parsed values.
 */
function companyParser(data: any): ICompany {
  // Extract and parse company information
  const parsed = {
    idIri: data['@id'], // Unique IRI identifier for the company
    id: getIdFromIdIri(data['@id']), // Extract numerical ID from IRI
    name: data.name ?? null, // Company name, defaulting to null if missing
    siret: data.siret ?? null, // SIRET number (French business identifier)
    address: data.address ?? null, // Street address of the company
    postalCode: data.zipcode ?? null, // Postal code (renamed from 'zipcode')
    city: data.city ?? null, // City where the company is located
    country: data.country ?? null, // Country of the company
    phone: data.phone ?? null, // Contact phone number
  };

  return parsed;
}

interface StatEvent {
  event: string;
  total: number;
  user: { '@id': string };
}
/**
 * Parses raw statistical event data and maps it to structured company statistics.
 *
 * @param {StatEvent[]} datas - The array of statistical events related to the company.
 * @param {Users} users - The list of users associated with the company.
 * @returns {CompanyStats} The structured company statistics parsed from the raw data.
 */
function companyStatsParser(datas: StatEvent[], users: Users): CompanyStats {
  /**
   * Updates the stats of a company based on the event type.
   */
  const updateCompanyStats = (
    stats: ICompanyStat[],
    idIri: string,
    value: StatEvent,
  ): ICompanyStat[] => {
    const index = stats.findIndex((stat) => stat.idIri === idIri);
    if (index === -1) return stats; // Avoid accessing invalid index

    switch (value.event) {
      case 'procedure_event':
        stats[index].procedureEvents = value.total;
        break;
      case 'ad_study':
        stats[index].adStudies = value.total;
        break;
      case 'comparative_study':
        stats[index].comparativeStudies = value.total;
        break;
      case 'auth_success':
        stats[index].connections = value.total;
        break;
      case 'active_research':
        stats[index].activeResearches = value.total;
        break;
      default:
        break;
    }

    return stats;
  };

  /**
   * Parses the dataset to return structured company statistics.
   */
  const parsedStats = datas.reduce((acc: ICompanyStat[], curr: StatEvent) => {
    const userIdIri = curr.user['@id'];
    let companyIndex = acc.findIndex((stat) => stat.idIri === userIdIri);

    if (companyIndex === -1) {
      // Find the user info
      const user = users.find((u) => u.idIri === userIdIri);

      // Create a new company stat entry
      acc.push({
        idIri: userIdIri,
        name: user?.fullName ?? '',
        activeResearches: 0,
        adStudies: 0,
        comparativeStudies: 0,
        connections: 0,
        procedureEvents: 0,
      });

      companyIndex = acc.length - 1; // Update the index after push
    }

    return updateCompanyStats(acc, userIdIri, curr);
  }, []);

  return parsedStats;
}

export { companyParser, companyStatsParser };
